import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface Slot {
  start: string;
  end: string;
}

interface FriendInfo {
  display_name: string;
  timezone: string;
}

interface AvailabilityInfo {
  friend: FriendInfo;
  slots: Slot[];
  activeGames: number;
  maxGames: number;
  canBook: boolean;
  legs: number;
  duration: number;
}

interface Format {
  id: string;
  name: string;
  gameType: string;
  defaultLegs: number;
  doubleIn: boolean;
  doubleOut: boolean;
}

interface League {
  id: string;
  name: string;
  abbreviation: string | null;
  formats: Format[];
}

// Group slots by date for display
interface SlotsByDate {
  [date: string]: {
    dateDisplay: string;
    slots: Slot[];
  };
}

export default function FriendBook() {
  const { friendId } = useParams<{ friendId: string }>();
  const navigate = useNavigate();

  const [availabilityInfo, setAvailabilityInfo] = useState<AvailabilityInfo | null>(null);
  const [friendName, setFriendName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  // League and format state
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [selectedFormatId, setSelectedFormatId] = useState('');
  const [leagueName, setLeagueName] = useState('');

  // Game options
  const [legs, setLegs] = useState(5);
  const [gameType, setGameType] = useState('501');
  const [doubleIn, setDoubleIn] = useState(false);
  const [doubleOut, setDoubleOut] = useState(true);
  const [notes, setNotes] = useState('');

  // Get formats for selected league
  const selectedLeague = leagues.find(l => l.id === selectedLeagueId);
  const availableFormats = selectedLeague?.formats || [];

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues');
      setLeagues(response.data.leagues || []);
    } catch {
      // Leagues are optional
    }
  };

  const fetchAvailability = async (selectedLegs: number) => {
    if (!friendId) return;

    setIsLoading(true);
    setError('');
    setSelectedSlot(null);

    try {
      const response = await api.get(`/friends/${friendId}/availability?legs=${selectedLegs}`);
      setAvailabilityInfo(response.data);
      setFriendName(response.data.friend?.display_name || 'Your Friend');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to load availability');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues();
    fetchAvailability(legs);
  }, [friendId]);

  // Handle legs change - refetch availability
  const handleLegsChange = (newLegs: number) => {
    setLegs(newLegs);
    fetchAvailability(newLegs);
  };

  // Handle league selection
  const handleLeagueChange = (leagueId: string) => {
    setSelectedLeagueId(leagueId);
    setSelectedFormatId('');
    const league = leagues.find(l => l.id === leagueId);
    setLeagueName(league?.name || '');
  };

  // Handle format selection - auto-populate settings
  const handleFormatChange = (formatId: string) => {
    setSelectedFormatId(formatId);
    const format = availableFormats.find(f => f.id === formatId);
    if (format) {
      setLegs(format.defaultLegs);
      setGameType(format.gameType);
      setDoubleIn(format.doubleIn);
      setDoubleOut(format.doubleOut);
      fetchAvailability(format.defaultLegs);
    }
  };

  // Group slots by date
  const groupSlotsByDate = (slots: Slot[]): SlotsByDate => {
    const grouped: SlotsByDate = {};

    slots.forEach(slot => {
      const date = parseISO(slot.start);
      const dateKey = format(date, 'yyyy-MM-dd');

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          dateDisplay: format(date, 'EEEE, MMMM d'),
          slots: []
        };
      }

      grouped[dateKey].slots.push(slot);
    });

    return grouped;
  };

  const handleBook = async () => {
    if (!selectedSlot || !friendId) return;

    setIsBooking(true);
    setError('');

    try {
      await api.post(`/friends/${friendId}/book`, {
        slotStart: selectedSlot.start,
        legs,
        leagueName: leagueName || undefined,
        leagueFormatId: selectedFormatId || undefined,
        gameType,
        doubleIn,
        doubleOut,
        notes: notes || undefined
      });
      setBookingComplete(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; code?: string } } };
      if (error.response?.data?.code === 'GAME_LIMIT_REACHED') {
        setError(`You have reached the maximum of ${availabilityInfo?.maxGames} active games with this friend.`);
      } else if (error.response?.data?.code === 'SLOT_CONFLICT') {
        setError('This time slot is no longer available. Please select another.');
        fetchAvailability(legs);
      } else {
        setError(error.response?.data?.error || 'Failed to book. Please try again.');
      }
    } finally {
      setIsBooking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !availabilityInfo) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-4xl mb-4">:(</div>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/friends')}
            className="btn-secondary"
          >
            Back to Friends
          </button>
        </div>
      </div>
    );
  }

  if (bookingComplete) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-4xl mb-4">🎯</div>
          <h1 className="text-xl font-bold text-green-400 mb-2">Game Request Sent!</h1>
          <p className="text-gray-400 mb-4">
            Your game request has been sent to {friendName}. They will be notified and can accept or decline.
          </p>
          {selectedSlot && (
            <div className="p-4 bg-[#252550] rounded-lg mb-4">
              <p className="font-medium text-white">
                {format(parseISO(selectedSlot.start), 'EEEE, MMMM d')}
              </p>
              <p className="text-gray-400">
                {format(parseISO(selectedSlot.start), 'h:mm a')} - {format(parseISO(selectedSlot.end), 'h:mm a')}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {leagueName || 'Casual'} - {gameType} - {legs} legs
              </p>
            </div>
          )}
          <button
            onClick={() => navigate('/friends')}
            className="btn-primary"
          >
            Back to Friends
          </button>
        </div>
      </div>
    );
  }

  const slotsByDate = availabilityInfo?.slots ? groupSlotsByDate(availabilityInfo.slots) : {};
  const hasSlots = Object.keys(slotsByDate).length > 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/friends')}
          className="text-gray-400 hover:text-white transition-colors"
        >
          &larr; Back to Friends
        </button>
      </div>

      <div className="card">
        <h1 className="text-2xl font-bold text-center text-[#1111d4] mb-2">
          Book a Game
        </h1>
        <p className="text-center text-gray-400">
          Schedule a match with <strong className="text-white">{friendName || 'your friend'}</strong>
        </p>

        {/* Game limit info */}
        {availabilityInfo && (
          <div className="mt-4 text-center text-sm">
            <p className={`${availabilityInfo.canBook ? 'text-gray-500' : 'text-yellow-400'}`}>
              {availabilityInfo.activeGames} of {availabilityInfo.maxGames} active games
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
          {error}
        </div>
      )}

      {!availabilityInfo?.canBook ? (
        <div className="card text-center py-8">
          <p className="text-yellow-400 mb-2">Game Limit Reached</p>
          <p className="text-gray-400 text-sm">
            You already have {availabilityInfo?.maxGames} active games with this friend.
            Wait for existing games to complete before booking more.
          </p>
        </div>
      ) : (
        <>
          {/* Game Options */}
          <div className="card">
            <h2 className="text-lg font-semibold text-white mb-4">Game Options</h2>

            {/* League Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  League
                </label>
                <select
                  value={selectedLeagueId}
                  onChange={(e) => handleLeagueChange(e.target.value)}
                  className="w-full px-3 py-2 bg-[#252550] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#1111d4]"
                >
                  <option value="">-- Casual (no league) --</option>
                  {leagues.map((league) => (
                    <option key={league.id} value={league.id}>
                      {league.name} {league.abbreviation ? `(${league.abbreviation})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Format
                </label>
                <select
                  value={selectedFormatId}
                  onChange={(e) => handleFormatChange(e.target.value)}
                  className={`w-full px-3 py-2 bg-[#252550] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#1111d4] ${!selectedLeagueId ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!selectedLeagueId}
                >
                  <option value="">-- Select format --</option>
                  {availableFormats.map((format) => (
                    <option key={format.id} value={format.id}>
                      {format.name} ({format.gameType}, {format.defaultLegs} legs)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Game Type, Legs, Double In/Out */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Game Type
                  {selectedFormatId && <span className="text-gray-500 text-xs ml-1">(format)</span>}
                </label>
                <select
                  value={gameType}
                  onChange={(e) => setGameType(e.target.value)}
                  className={`w-full px-3 py-2 bg-[#252550] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#1111d4] ${selectedFormatId ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!!selectedFormatId}
                >
                  <option value="501">501</option>
                  <option value="301">301</option>
                  <option value="701">701</option>
                  <option value="Cricket">Cricket</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Legs
                </label>
                <select
                  value={legs}
                  onChange={(e) => handleLegsChange(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-[#252550] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#1111d4]"
                >
                  {[...Array(21)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col justify-end pb-2">
                <label className={`flex items-center gap-2 text-gray-300 ${selectedFormatId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={doubleIn}
                    onChange={(e) => setDoubleIn(e.target.checked)}
                    className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
                    disabled={!!selectedFormatId}
                  />
                  <span className="text-sm">Double In</span>
                </label>
              </div>
              <div className="flex flex-col justify-end pb-2">
                <label className={`flex items-center gap-2 text-gray-300 ${selectedFormatId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={doubleOut}
                    onChange={(e) => setDoubleOut(e.target.checked)}
                    className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
                    disabled={!!selectedFormatId}
                  />
                  <span className="text-sm">Double Out</span>
                </label>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              Game duration: ~{availabilityInfo?.duration || (legs <= 10 ? 30 : 45)} minutes
            </p>

            {/* Notes (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional info..."
                rows={2}
                className="w-full px-3 py-2 bg-[#252550] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#1111d4] resize-none"
              />
            </div>
          </div>

          {/* Time Slot Selection */}
          {!hasSlots ? (
            <div className="card text-center py-8">
              <p className="text-gray-400 mb-2">No Available Slots</p>
              <p className="text-gray-500 text-sm">
                {friendName || 'Your friend'} doesn't have any available slots in the next 14 days.
                They may need to set up their availability.
              </p>
            </div>
          ) : (
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Select a Time Slot</h2>
              <p className="text-sm text-gray-500 mb-4">
                Showing {friendName}'s available times for the next 14 days
              </p>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {Object.entries(slotsByDate).map(([dateKey, { dateDisplay, slots }]) => (
                  <div key={dateKey}>
                    <h3 className="font-medium text-gray-300 mb-2 sticky top-0 bg-[#1a1a3e] py-1">
                      {dateDisplay}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {slots.map((slot) => {
                        const isSelected = selectedSlot?.start === slot.start;
                        return (
                          <button
                            key={slot.start}
                            onClick={() => setSelectedSlot(slot)}
                            className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                              isSelected
                                ? 'bg-[#1111d4] text-white'
                                : 'bg-[#252550] hover:bg-[#2a2a5a] text-gray-300'
                            }`}
                          >
                            {format(parseISO(slot.start), 'h:mm a')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Confirm Booking */}
          {selectedSlot && (
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Confirm Game Request</h2>
              <div className="p-4 bg-[#1111d4]/20 border border-[#1111d4]/30 rounded-lg mb-4">
                <p className="font-medium text-[#1111d4]">
                  {format(parseISO(selectedSlot.start), 'EEEE, MMMM d')}
                </p>
                <p className="text-[#1111d4]/80">
                  {format(parseISO(selectedSlot.start), 'h:mm a')} - {format(parseISO(selectedSlot.end), 'h:mm a')}
                </p>
                <p className="text-sm text-[#1111d4]/60 mt-1">
                  {leagueName || 'Casual'} - {gameType} - {legs} legs
                  {doubleIn && ' - DI'}
                  {doubleOut && ' - DO'}
                </p>
              </div>
              <button
                onClick={handleBook}
                disabled={isBooking}
                className="btn-primary w-full"
              >
                {isBooking ? 'Sending Request...' : 'Send Game Request'}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                {friendName} will need to accept this request
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
